import React from 'react';
import { TextboxControl } from '../../../controls/TextboxControl';
import { DataSourceDropDownControl } from '../../../controls/DataSourceDropDownControl';
import { DataSourceChecklistControl } from '../../../controls/DataSourceChecklistControl';
import { RuleTargetDefinitionSelector } from './RuleDefinitionSelector';
import { InputControlType } from './Rule';
import { Spacer } from '../../../components/Containers';
import { RuleTargetAffinity, OffsetReferencePoints } from './RuleTargetConstants';
import { DropdownControl } from '../../../controls/DropdownControl';

const OFFSET_REGEX = '(-)?[0-9]*(\\.[0-9]{0,4})?';

function ConstraintSection({ referencePoint, onReferencePointChange, offsetMin, onOffsetMinChange, offsetMax, onOffsetMaxChange }) {
    return (
        <div className="rule-section">
            <h3>Constraint</h3>
            <div className="rule-field">
                <label>Reference Point</label>
                <DropdownControl items={OffsetReferencePoints} value={referencePoint} onChange={onReferencePointChange} valueMemberName='id' displayMemberName='name' />
            </div>
            <div className="rule-field rule-field-inline">
                <div>
                    <label>Min offset (in/mm)</label>
                    <TextboxControl regex={OFFSET_REGEX} onChange={onOffsetMinChange} value={offsetMin} />
                </div>
                <div>
                    <label>Max offset (in/mm)</label>
                    <TextboxControl regex={OFFSET_REGEX} onChange={onOffsetMaxChange} value={offsetMax} />
                </div>
            </div>
        </div>
    );
}

function LocationSection({ location, onLocationChange, onRelatedFeatureChange, onOffsetChange, onControlInit }) {
    return (
        <div className="rule-section">
            <h3>Location</h3>
            <div className={`rule-option ${location == 1 ? 'selected' : ''}`}>
                <div className="form-radio">
                    <input className="form-radio-input" type="radio" id='location_1' value={1} checked={location == 1} onChange={onLocationChange} />
                    <label className="form-radio-label" htmlFor='location_1'>Independent</label>
                </div>
            </div>
            <div className={`rule-option ${location == 2 ? 'selected' : ''}`}>
                <div className="form-radio">
                    <input className="form-radio-input" type="radio" id='location_2' value={2} checked={location == 2} onChange={onLocationChange} />
                    <label className="form-radio-label" htmlFor='location_2'>Centered To (if applicable)</label>
                </div>
                {location == 2 &&
                    <div className="rule-option-details">
                        <DataSourceDropDownControl id='related_feature' onInit={onControlInit} disabled={location != 2} required={location == 2} dataSourceName={'relatedFeatures'} onChange={onRelatedFeatureChange} />
                        <div>
                            <label>With Offset (in/mm)</label>
                            <TextboxControl required={location == 2} disabled={location != 2} regex={OFFSET_REGEX} onChange={onOffsetChange} value={'0.00'} />
                        </div>
                    </div>
                }
            </div>
            <div className={`rule-option ${location == 3 ? 'selected' : ''}`}>
                <div className="form-radio">
                    <input className="form-radio-input" type="radio" id='location_3' value={3} checked={location == 3} onChange={onLocationChange} />
                    <label className="form-radio-label" htmlFor='location_3'>Fix Offset from Pouch Top</label>
                </div>
                {location == 3 &&
                    <div className="rule-option-details">
                        <TextboxControl required={location == 3} disabled={location != 3} regex={OFFSET_REGEX} onChange={onOffsetChange} value={'0.00'} />
                    </div>
                }
            </div>
        </div>
    );
}

export class RuleTargetPopupContent extends React.Component {

    constructor(props) {
        super(props);

        if (!this.props.ruleDataManager) {
            throw new Error("RuleDataManager Property is required");
        }
        this.ruleDataManager = this.props.ruleDataManager;
        this.control = null;
        this.definition = null;
        this.values = this.props.values ? this.props.values : [];
        this.state = {
            affinity: 0,
            location: 1,
            offset: 0,
            relatedFeature: null,
            definition: this.definition,
            values: this.values,
            items: [],
            referencePoint: 0,
            offsetMin: '',
            offsetMax: ''
        }

        this.onDefinitionSelected = this.onDefinitionSelected.bind(this);
        this.onItemChange = this.onItemChange.bind(this);
        this.onCollectionChange = this.onCollectionChange.bind(this);
        this.onAddClick = this.onAddClick.bind(this);
        this.onAffinityChange = this.onAffinityChange.bind(this);
        this.onLocationChange = this.onLocationChange.bind(this);
        this.onOffsetChange = this.onOffsetChange.bind(this);
        this.onRelatedFeatureChange = this.onRelatedFeatureChange.bind(this);
        this.onReferencePointChange = this.onReferencePointChange.bind(this);
        this.onOffsetMinChange = this.onOffsetMinChange.bind(this);
        this.onOffsetMaxChange = this.onOffsetMaxChange.bind(this);
    }

    reset() {
        this.values = this.props.values ? this.props.values : [];
        this.state = {
            affinity: 0,
            values: this.values,
            items: [],
            location: 1,
            offset: 0,
            relatedFeature: null,
            referencePoint: 0,
            offsetMin: '',
            offsetMax: ''
        }
    }

    onDefinitionSelected(value, definition) {
        this.reset();
        this.definition = definition;
        this.setState({
            definition: this.definition,
            location: 1,
            affinity: 0
        });

        if (this.control && this.definition) {
            this.control.setDataSource(this.definition.dataSourceName);
        }
    }

    onItemChange(value, item) {
        var values = [];
        values.push(value);
        var items = [];
        items.push(item);

        this.setState({
            values: values,
            items: items
        });
    }

    onAffinityChange(event) {
        this.setState({
            affinity: event.target.value
        });
    }

    onCollectionChange(values, items) {
        this.setState({
            values: values,
            items: items
        });
    }

    onLocationChange(event) {
        this.setState({
            location: event.target.value
        });
    }

    onRelatedFeatureChange(value) {
        this.setState({
            relatedFeature: value
        });
    }

    onOffsetChange(value) {
        this.setState({
            offset: value
        });
    }

    onReferencePointChange(referencePointId) {
        this.setState({
            referencePoint: referencePointId
        });
    }

    onOffsetMinChange(value) {
        this.setState({ offsetMin: value });
    }

    onOffsetMaxChange(value) {
        this.setState({ offsetMax: value });
    }

    getControl() {
        if (this.definition) {
            switch (this.definition.inputControlType) {
                case InputControlType.FreeTextItem:
                    return (<p>Text Controls are not supported in this context</p>);
                case InputControlType.FreeTextTuple:
                    return (<p>Text Controls are not supported in this context</p>);
                case InputControlType.SingleChoice:
                    return (<DataSourceDropDownControl onInit={ctrl => this.control = ctrl} dataSourceName={this.definition.dataSourceName} onChange={this.onItemChange} />);
                case InputControlType.MultipleChoice:
                    return (<DataSourceChecklistControl onInit={ctrl => this.control = ctrl} dataSourceName={this.definition.dataSourceName} onChange={this.onCollectionChange} />);
                default:
                    throw new Error(`Input Control Type ${this.definition.inputControlType} is not supported`);
            }
        }
    }

    onAddClick() {
        this.props.modal.close();
        if (this.props.onAddClick) {
            var affinity = parseInt(this.state.affinity);

            var target;
            if (affinity === RuleTargetAffinity.ForbiddenRange) {
                target = {
                    definition: this.definition,
                    items: [],
                    affinity: affinity,
                    location: parseInt(this.state.referencePoint),
                    relatedFeature: 0,
                    offset: this.state.offsetMin !== '' ? parseFloat(this.state.offsetMin) : null,
                    offsetTo: this.state.offsetMax !== '' ? parseFloat(this.state.offsetMax) : null
                };
            } else {
                target = {
                    definition: this.definition,
                    items: this.state.items,
                    affinity: affinity,
                    location: parseInt(this.state.location),
                    relatedFeature: this.state.location == 2 ? parseInt(this.state.relatedFeature) : 0,
                    offset: (this.state.location == 2 || this.state.location == 3) ? parseFloat(this.state.offset) : 0,
                    offsetTo: null
                };
            }

            this.props.onAddClick(target);
        }
    }

    isValid() {
        var affinity = parseInt(this.state.affinity);

        if (affinity === RuleTargetAffinity.ForbiddenRange) {
            var hasRef = parseInt(this.state.referencePoint) > 0;
            var hasAtLeastOneValue = this.state.offsetMin !== '' || this.state.offsetMax !== '';
            return hasRef && hasAtLeastOneValue;
        }

        if (affinity > 0) {
            if (parseInt(this.state.location) == 2) {
                return this.state.values && this.state.values.length > 0 && parseInt(this.state.relatedFeature) > 0;
            }
            return this.state.values && this.state.values.length > 0;
        }

        return false;
    }

    render() {
        var self = this;
        var affinity = parseInt(this.state.affinity);
        var isForbiddenRange = affinity === RuleTargetAffinity.ForbiddenRange;

        return (
            <div className='ruleTargetPopupContent rulePopup'>
                <div className="rule-section">
                    <h3>Target Definition</h3>
                    <RuleTargetDefinitionSelector id='feature' ruleDataManager={this.ruleDataManager} onChange={this.onDefinitionSelected} />
                </div>

                {this.state.definition &&
                    <div className='inner-content'>
                        <div className="rule-section">
                            <h3>Affinity</h3>
                            <div className={`rule-option ${this.state.affinity == 1 ? 'selected' : ''}`}>
                                <div className="form-radio">
                                    <input className="form-radio-input" type="radio" id='affinity_1' value={1} checked={this.state.affinity == 1} onChange={self.onAffinityChange} />
                                    <label className="form-radio-label" htmlFor='affinity_1'>Exclusive</label>
                                </div>
                            </div>
                            <div className={`rule-option ${this.state.affinity == 2 ? 'selected' : ''}`}>
                                <div className="form-radio">
                                    <input className="form-radio-input" type="radio" id='affinity_2' value={2} checked={this.state.affinity == 2} onChange={self.onAffinityChange} />
                                    <label className="form-radio-label" htmlFor='affinity_2'>Include</label>
                                </div>
                            </div>
                            <div className={`rule-option ${this.state.affinity == 3 ? 'selected' : ''}`}>
                                <div className="form-radio">
                                    <input className="form-radio-input" type="radio" id='affinity_3' value={3} checked={this.state.affinity == 3} onChange={self.onAffinityChange} />
                                    <label className="form-radio-label" htmlFor='affinity_3'>Exclude</label>
                                </div>
                            </div>
                            {this.state.definition.allowForbiddenRange &&
                                <div className={`rule-option ${this.state.affinity == 4 ? 'selected' : ''}`}>
                                    <div className="form-radio">
                                        <input className="form-radio-input" type="radio" id='affinity_4' value={4} checked={this.state.affinity == 4} onChange={self.onAffinityChange} />
                                        <label className="form-radio-label" htmlFor='affinity_4'>Forbidden Range</label>
                                    </div>
                                </div>
                            }
                        </div>

                        {isForbiddenRange
                            ? <ConstraintSection
                                referencePoint={this.state.referencePoint}
                                onReferencePointChange={this.onReferencePointChange}
                                offsetMin={this.state.offsetMin}
                                onOffsetMinChange={this.onOffsetMinChange}
                                offsetMax={this.state.offsetMax}
                                onOffsetMaxChange={this.onOffsetMaxChange}
                              />
                            : <div className="rule-section">
                                <h3>Items</h3>
                                {self.getControl()}
                              </div>
                        }

                        {!isForbiddenRange &&
                            <LocationSection
                                location={this.state.location}
                                onLocationChange={this.onLocationChange}
                                onRelatedFeatureChange={this.onRelatedFeatureChange}
                                onOffsetChange={this.onOffsetChange}
                                onControlInit={ctrl => this.control = ctrl}
                              />
                        }
                    </div>
                }

                <Spacer />

                <div className='modal-footer'>
                    <button className='btn btn-lg btn-primary' disabled={!this.isValid()} onClick={this.onAddClick}>Add</button>
                    <button className='btn btn-lg btn-secondary pull-right' onClick={this.props.modal.close}>Cancel</button>
                </div>
            </div>
        );
    }
}